import React, { useCallback, useEffect, useState } from "react";
import { Ban, CheckCircle2, Loader2, RefreshCw, Undo2, Search, Calendar } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { useConfirm } from "./ConfirmDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

/**
 * Phase 101dw — Nettoyage des ventes fantômes Nayax (transactions annulées
 * par la machine mais synchronisées à tort en AM avant Phase 101dw).
 *
 * Cible : sales AM avec source=NAYAX et unit_price=0 (Nayax n'a pas encaissé
 * car blocage mécanique / produit non distribué).
 *
 * Action : restore la qty au lot + crée un mouvement IN de compensation +
 * supprime la sale fantôme.
 */
export default function PhantomNayaxSalesPanel() {
  const [hours, setHours] = useState(72);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [lastReport, setLastReport] = useState(null);
  const confirm = useConfirm();

  const loadAudit = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/nayax/phantom-sales-audit", { params: { hours } });
      setItems(data.items || []);
      setSelected(new Set((data.items || []).map((s) => s.sale_id)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur audit ventes fantômes");
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  const toggleKey = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runCleanup = async () => {
    if (selected.size === 0) {
      toast.error("Sélectionne au moins une vente à nettoyer");
      return;
    }
    const totalQty = items.filter((s) => selected.has(s.sale_id)).reduce((a, s) => a + (s.quantity || 0), 0);
    const ok = await confirm({
      title: "Nettoyer les ventes fantômes ?",
      description: `Supprimer ${selected.size} vente(s) fantôme(s) totalisant ${totalQty} barquette(s) à restaurer au stock.\n\nLes mouvements OUT initiaux seront marqués "reversed" (traçabilité HACCP préservée). Un mouvement IN de compensation sera créé pour chaque restauration.\n\nCette action est irréversible.`,
      confirmText: `Nettoyer ${selected.size} vente(s)`,
    });
    if (!ok) return;
    setCleaning(true);
    try {
      const { data } = await api.post("/nayax/phantom-sales-cleanup", {
        hours,
        sale_ids: Array.from(selected),
      });
      setLastReport(data);
      toast.success(`✅ ${data.restored_count} vente(s) supprimée(s) · ${data.total_qty_restored} barquette(s) restaurée(s) au stock`);
      await loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur nettoyage");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="border border-[#C3D1E8] bg-[#F0F5FB] rounded-lg" data-testid="phantom-nayax-panel">
      <div className="p-4 border-b border-[#C3D1E8]">
        <div className="flex items-center gap-2 mb-1">
          <Ban className="w-5 h-5 text-[#2F5A8C]" />
          <h3 className="font-semibold text-[#1A3D5A]">Nettoyer les ventes fantômes Nayax (Phase 101dw)</h3>
        </div>
        <p className="text-xs text-[#2F5A8C] leading-relaxed">
          Détecte 2 types de ventes AM à nettoyer :<br/>
          <b>A · TX annulée machine</b> — vente créée avec <code className="bg-white px-1 rounded mx-1">unit_price=0</code> (Nayax n&apos;a pas encaissé, blocage). Depuis Phase 101dw la sync les ignore, mais celles déjà en base restent à nettoyer.<br/>
          <b>B · Slot vide (orphan)</b> — un client tape le code d&apos;un slot physiquement vide en AM ; Nayax envoie la tx mais AM crée une vente sans lot (<code className="bg-white px-1 rounded mx-1">lot_id=None</code>). Le stock physique n&apos;est PAS impacté mais le CA est faussé.
        </p>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs mb-1 block">Fenêtre (heures)</Label>
            <Input
              type="number"
              min="1"
              max="720"
              value={hours}
              onChange={(e) => setHours(Math.max(1, Number(e.target.value) || 72))}
              className="h-9 w-24"
              data-testid="phantom-nayax-hours"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadAudit}
            disabled={loading}
            className="h-9"
            data-testid="phantom-nayax-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Actualiser
          </Button>
          <div className="flex-1" />
          <div className="text-xs text-[#1A3D5A]">
            <span className="font-semibold">{items.length}</span> vente(s) fantôme(s) à unit_price = 0
          </div>
        </div>

        {items.length === 0 && !loading && (
          <div className="bg-[#EAF4EE] border border-[#5A8F76] rounded-md p-3 text-sm text-[#1F4E3D] flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Aucune vente fantôme Nayax détectée sur cette fenêtre.
          </div>
        )}

        {items.length > 0 && (
          <>
            <div className="border border-[#E5E5E0] rounded-md bg-white overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#FAFAF7] border-b border-[#E5E5E0]">
                  <tr>
                    <th className="text-left px-2 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={selected.size === items.length}
                        onChange={(e) => setSelected(e.target.checked ? new Set(items.map((s) => s.sale_id)) : new Set())}
                        data-testid="phantom-nayax-select-all"
                      />
                    </th>
                    <th className="text-left px-2 py-2">Machine</th>
                    <th className="text-left px-2 py-2">Produit</th>
                    <th className="text-left px-2 py-2">Lot</th>
                    <th className="text-center px-2 py-2 w-14">Qté</th>
                    <th className="text-left px-2 py-2">Nayax TX</th>
                    <th className="text-left px-2 py-2">Type</th>
                    <th className="text-left px-2 py-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => {
                    const typeMeta = {
                      cancelled_zero: { label: "TX annulée (prix 0)", color: "bg-orange-100 text-orange-800 border-orange-300" },
                      orphan_slot_vide: { label: "Slot vide (orphan)", color: "bg-purple-100 text-purple-800 border-purple-300" },
                      cancelled_orphan: { label: "Annulée + orphan", color: "bg-rose-100 text-rose-800 border-rose-300" },
                    }[s.phantom_type] || { label: s.phantom_type, color: "bg-neutral-100 text-neutral-700" };
                    return (
                    <tr
                      key={s.sale_id}
                      className="border-t border-[#E5E5E0] hover:bg-[#FAFAF7]"
                      data-testid={`phantom-nayax-row-${s.external_id}`}
                    >
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={selected.has(s.sale_id)}
                          onChange={() => toggleKey(s.sale_id)}
                        />
                      </td>
                      <td className="px-2 py-2">{s.distributor_name}</td>
                      <td className="px-2 py-2">
                        <div>{s.product_name}</div>
                        <div className="text-[10px] text-[#6B6E6B] font-mono">{s.product_sku}</div>
                      </td>
                      <td className="px-2 py-2 font-mono">{s.lot_number || <span className="italic text-neutral-500">—</span>}</td>
                      <td className="px-2 py-2 text-center font-bold text-[#C05C47]">{s.quantity}</td>
                      <td className="px-2 py-2 font-mono text-[10px]">{s.external_id}</td>
                      <td className="px-2 py-2"><Badge className={`${typeMeta.color} text-[10px]`}>{typeMeta.label}</Badge></td>
                      <td className="px-2 py-2 text-[10px] text-[#6B6E6B]">
                        {s.sold_at ? new Date(s.sold_at).toLocaleString("fr-CH") : "?"}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-2 pt-2">
              <Badge variant="outline" className="bg-white">
                {selected.size} vente(s) sélectionnée(s)
              </Badge>
              <Button
                onClick={runCleanup}
                disabled={cleaning || selected.size === 0}
                className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
                data-testid="phantom-nayax-cleanup-btn"
              >
                {cleaning ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Undo2 className="w-4 h-4 mr-1" />}
                Nettoyer &amp; restaurer le stock
              </Button>
            </div>
          </>
        )}

        {lastReport && (
          <div className="mt-4 border border-[#5A8F76] bg-[#EAF4EE] rounded-md p-3 text-xs" data-testid="phantom-nayax-report">
            <div className="font-semibold text-[#1F4E3D] mb-2 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" /> Rapport de nettoyage
            </div>
            <div className="grid grid-cols-2 gap-2 text-[#1F4E3D]">
              <div>
                <span className="text-[10px] uppercase text-[#6B6E6B]">Ventes supprimées</span>
                <div className="font-mono text-lg">{lastReport.restored_count}</div>
              </div>
              <div>
                <span className="text-[10px] uppercase text-[#6B6E6B]">Barquettes restaurées</span>
                <div className="font-mono text-lg">{lastReport.total_qty_restored}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ============== Diagnostic manuel ============== */}
      <ManualDiagnosticSection />
    </div>
  );
}


function ManualDiagnosticSection() {
  const [distributors, setDistributors] = useState([]);
  const [distId, setDistId] = useState("");
  const [date, setDate] = useState(new Date(Date.now() - 86400000).toISOString().slice(0, 10));  // Hier par défaut
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  const confirm = useConfirm();

  useEffect(() => {
    api.get("/distributors").then(({ data }) => {
      setDistributors((data || []).filter((d) => d.type === "AUTOMATIQUE" && !d.archived));
    }).catch(() => {});
  }, []);

  const load = async () => {
    if (!distId || !date) return;
    setLoading(true);
    setItems([]);
    setSelected(new Set());
    try {
      const { data } = await api.get("/nayax/sales-diagnostic", { params: { distributor_id: distId, date } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur diagnostic");
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const deleteSelected = async () => {
    if (selected.size === 0) { toast.error("Sélectionnez au moins une vente à supprimer"); return; }
    const total = items.filter((s) => selected.has(s.sale_id)).reduce((a, s) => a + (s.quantity || 0), 0);
    const ok = await confirm({
      title: "Supprimer ces ventes ?",
      description: `${selected.size} vente(s) sélectionnée(s) totalisant ${total} barquette(s). Si un lot était décrémenté, le stock sera restauré (mouvement IN de compensation, traçabilité HACCP préservée).\n\nAction irréversible.`,
      confirmText: "Supprimer",
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/nayax/sales-diagnostic/delete", { sale_ids: Array.from(selected) });
      setLastReport(data);
      toast.success(`${data.restored_count} vente(s) supprimée(s) · ${data.total_qty_restored} barquette(s) restaurée(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4 border-t border-[#C3D1E8]">
      <div className="flex items-center gap-2 mb-2">
        <Search className="w-4 h-4 text-[#2F5A8C]" />
        <h4 className="font-semibold text-[#1A3D5A] text-sm">Diagnostic manuel · Sélectionner des ventes précises</h4>
      </div>
      <p className="text-[11px] text-[#2F5A8C] leading-relaxed mb-3">
        Si l&apos;audit automatique ne détecte pas vos fantômes (ex. vente créée avec un lot d&apos;un autre slot), utilisez ce diagnostic pour lister TOUTES les ventes Nayax d&apos;une machine sur un jour, et pointer manuellement celles à supprimer.
      </p>

      <div className="flex items-end gap-2 flex-wrap mb-3">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Machine</Label>
          <Select value={distId} onValueChange={setDistId}>
            <SelectTrigger className="h-9" data-testid="diag-machine"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>
              {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-40" data-testid="diag-date" />
        </div>
        <Button onClick={load} disabled={loading || !distId} size="sm" className="bg-[#1F4E3D] hover:bg-[#173B2E] h-9" data-testid="diag-load">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4 mr-1" />}
          Voir les ventes
        </Button>
      </div>

      {items.length === 0 && distId && !loading && (
        <div className="text-xs text-[#575A57] italic bg-white border border-[#E5E5E0] rounded p-3">
          Aucune vente Nayax pour cette machine ce jour-là.
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="text-xs text-[#1A3D5A] mb-2">
            <span className="font-semibold">{items.length}</span> vente(s) trouvée(s) · CA total {items.reduce((a, s) => a + (s.unit_price * s.quantity || 0), 0).toFixed(2)} CHF
          </div>
          <div className="border border-[#E5E5E0] rounded bg-white overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#FAFAF7] border-b border-[#E5E5E0]">
                <tr>
                  <th className="text-left px-2 py-2 w-8">
                    <input
                      type="checkbox"
                      onChange={(e) => setSelected(e.target.checked ? new Set(items.map((s) => s.sale_id)) : new Set())}
                    />
                  </th>
                  <th className="text-left px-2 py-2">Heure</th>
                  <th className="text-left px-2 py-2">Produit</th>
                  <th className="text-left px-2 py-2">Lot · slot</th>
                  <th className="text-center px-2 py-2">Qté</th>
                  <th className="text-right px-2 py-2">Prix</th>
                  <th className="text-left px-2 py-2">Paiement</th>
                  <th className="text-left px-2 py-2">Nayax TX</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr
                    key={s.sale_id}
                    className={`border-t border-[#E5E5E0] hover:bg-[#FAFAF7] ${selected.has(s.sale_id) ? "bg-red-50" : ""}`}
                    data-testid={`diag-row-${s.external_id}`}
                  >
                    <td className="px-2 py-2">
                      <input type="checkbox" checked={selected.has(s.sale_id)} onChange={() => toggle(s.sale_id)} />
                    </td>
                    <td className="px-2 py-2 font-mono text-[10px]">{s.sold_at ? new Date(s.sold_at).toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "?"}</td>
                    <td className="px-2 py-2">
                      <div>{s.product_name}</div>
                      <div className="text-[10px] text-[#6B6E6B] font-mono">{s.product_sku}</div>
                    </td>
                    <td className="px-2 py-2 font-mono text-[10px]">
                      {s.lot_number || <span className="italic text-neutral-500">orphan</span>}
                      {s.slot_number != null && <span className="text-[#6B6E6B]"> · slot {s.slot_number}</span>}
                    </td>
                    <td className="px-2 py-2 text-center">{s.quantity}</td>
                    <td className="px-2 py-2 text-right font-mono">{(s.unit_price || 0).toFixed(2)}</td>
                    <td className="px-2 py-2 text-[10px]">{s.payment_method || "—"}</td>
                    <td className="px-2 py-2 font-mono text-[9px]">{s.external_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-3 gap-2">
            <Badge variant="outline" className="bg-white">
              {selected.size} vente(s) sélectionnée(s)
            </Badge>
            <Button
              onClick={deleteSelected}
              disabled={deleting || selected.size === 0}
              size="sm"
              variant="destructive"
              data-testid="diag-delete"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Undo2 className="w-4 h-4 mr-1" />}
              Supprimer &amp; restaurer stock
            </Button>
          </div>

          {lastReport && (
            <div className="mt-3 border border-[#5A8F76] bg-[#EAF4EE] rounded p-2 text-xs">
              ✅ {lastReport.restored_count} vente(s) supprimée(s), {lastReport.total_qty_restored} barquette(s) restaurée(s) au stock
            </div>
          )}
        </>
      )}
    </div>
  );
}
